import type { LevelIdentity } from '../model/identity';
import { stableHash64, toHex64 } from '../model/hash';
import { Pcg32 } from './prng';

/**
 * Namespace constant. Changing this reseeds the entire universe of levels.
 *
 * It still reads `outbound` because that is what the game was called when the seeds were
 * fixed. The string is an opaque hash input that no player ever sees, and renaming it would
 * silently replace every board in the campaign — so it is deliberately left alone. Do not
 * "tidy" it.
 */
export const SEED_NAMESPACE = 'outbound.seed.v1';

/**
 * Independent random streams. Splitting by subsystem means a cosmetic change (which only
 * consumes the `cosmetic` stream) can never shift puzzle topology.
 */
export const SUBSYSTEM = {
  stencilSelection: 'stencil.selection',
  stencilTransform: 'stencil.transform',
  gridDimensions: 'grid.dimensions',
  archetype: 'level.archetype',
  recipe: 'level.recipe',
  reverseConstruction: 'generate.reverse',
  candidateRetry: 'generate.retry',
  holeFilling: 'generate.holes',
  direction: 'generate.direction',
  cosmetic: 'cosmetic.variation',
  hint: 'hint.selection',
} as const;

export type SubsystemName = (typeof SUBSYSTEM)[keyof typeof SUBSYSTEM];

export function deriveRootSeed(identity: LevelIdentity): bigint {
  return stableHash64(
    SEED_NAMESPACE,
    identity.campaignId,
    identity.levelNumber,
    identity.generatorVersion,
    identity.stencilLibraryVersion,
    identity.contentVersion,
  );
}

export function rootSeedHex(identity: LevelIdentity): string {
  return toHex64(deriveRootSeed(identity));
}

export function deriveSubSeed(rootSeed: bigint, subsystem: string, index = 0): bigint {
  return stableHash64(rootSeed, subsystem, index);
}

/** Convenience: a fresh PRNG positioned at the start of an isolated stream. */
export function rngFor(rootSeed: bigint, subsystem: string, index = 0): Pcg32 {
  return new Pcg32(deriveSubSeed(rootSeed, subsystem, index));
}
